//src/types/Store/Aircraft/aircraftInterfaces.ts

import { AircraftClass } from '@/types/Account/AccountEnums'
import { AircraftType, Category, CurrencyType } from '@/types/Helper/Enums'
import { Timestamp } from 'firebase/firestore'
import { DEFAULT_TACH_PRECISION, type TachPrecision } from '@/utils/readings'

// MaintenanceToDate Interface
export interface MaintenanceToDate {
  hobbs: number // This should be the current hobbs reading of the aircraft
  tach: number // This should be the current tach reading of the aircraft

  airframeInfo: InspectionData | null
  engineInfo: InspectionData | null
  propInfo: InspectionData | null

  gpsExpirationDate: Date | null

  eltBatteryExpDate: Date | null
  eltInspection: Date | null

  pitotStaticInspection: Date | null
  transponderInspection: Date | null

  engineReplaceTach: number[]
  propReplaceTach: number[]

  tachReplaceTach: number
  hobbsReplaceTach: number

  inMaintenance: boolean
}

export interface DateField {
  /** How this shows up in the UI */
  label: string
  /** The exact key on MaintenanceToDate */
  path: string
}

export const maintenanceDateFields: DateField[] = [
  { label: 'GPS Expiration Date', path: 'gpsExpirationDate' },
  { label: 'ELT Inspection Date', path: 'eltInspection' },
  { label: 'ELT Battery Expiration', path: 'eltBatteryExpDate' },
  { label: 'Pitot Static Inspection', path: 'pitotStaticInspection' },
  { label: 'Transponder Inspection', path: 'transponderInspection' },
  { label: 'Airframe Annual Date', path: 'airframeInfo.annualDate' },
  { label: 'Airframe 100-Hour Date', path: 'airframeInfo.hundredHour' },
  { label: 'Airframe 50-Hour Date', path: 'airframeInfo.fiftyHour' },

  { label: 'Engine Annual Date', path: 'engineInfo.annualDate' },
  { label: 'Engine 100-Hour Date', path: 'engineInfo.hundredHour' },
  { label: 'Engine 50-Hour Date', path: 'engineInfo.fiftyHour' },

  { label: 'Prop Annual Date', path: 'propInfo.annualDate' },
  { label: 'Prop 100-Hour Date', path: 'propInfo.hundredHour' },
  { label: 'Prop 50-Hour Date', path: 'propInfo.fiftyHour' }
]

// Create MaintenanceToDate
export function createMaintenanceToDate(init: Partial<MaintenanceToDate> = {}): MaintenanceToDate {
  return {
    hobbs: 0,
    tach: 0,
    airframeInfo: createInspectionData(),
    engineInfo: createInspectionData(),
    propInfo: createInspectionData(),
    gpsExpirationDate: null,
    eltInspection: null,
    eltBatteryExpDate: null,
    pitotStaticInspection: null,
    transponderInspection: null,
    engineReplaceTach: [0],
    propReplaceTach: [0],
    tachReplaceTach: 0,
    hobbsReplaceTach: 0,
    inMaintenance: false,
    ...init
  }
}

// InspectionData Interface
export interface InspectionData {
  annualDate: Date | null
  annualTach: number
  annualHobbs: number
  hundredHour: Date | null
  hundredHourTach: number
  hundredHourHobbs: number
  fiftyHour: Date | null
  fiftyHourTach: number
  fiftyHourHobbs: number
}

// Create InspectionData
export function createInspectionData(init: Partial<InspectionData> = {}): InspectionData {
  return {
    annualDate: null,
    annualTach: 0,
    annualHobbs: 0,
    hundredHour: null,
    hundredHourTach: 0,
    hundredHourHobbs: 0,
    fiftyHour: null,
    fiftyHourTach: 0,
    fiftyHourHobbs: 0,
    ...init
  }
}

export enum AdminFeeMode {
  None = 'None',
  Monthly = 'Monthly', // flat fee, billed on its own cycle — NOT per-rental
  Hourly = 'Hourly', // flat $/hour × hours flown
  Percent = 'Percent' // percent of the hourly rental RATE (scales with price changes)
}

// after
export interface Admin {
  stores: string[]
  isPart135: boolean
  isForHire: boolean
  isExternal: boolean
  isGrounded: boolean
  isTachBilling: boolean
  isActive: boolean
  isTransient: boolean
  isPart141: boolean
  isPart61: boolean
  tachHobbes: TachHobbesOptions
  autoUpdateEngine: boolean
  autoUpdateProp: boolean
  adminFeeMode: AdminFeeMode
  adminFee: number
  hourlyFee: number
  hourlyFeePercent: number
  leasePercent: number
  do50Hr: boolean
  do100Hr: boolean
  doPitotStatic: boolean
}

export enum TachHobbesOptions {
  Tach,
  Hobbs,
  Both
}

// after
export function createAdmin(init: Partial<Admin> = {}): Admin {
  return {
    stores: [],
    isPart135: false,
    isForHire: false,
    isExternal: false,
    isGrounded: false,
    isTachBilling: false,
    isActive: true,
    isTransient: false,
    isPart141: false,
    isPart61: false,
    tachHobbes: TachHobbesOptions.Both,
    autoUpdateEngine: false,
    autoUpdateProp: false,

    adminFeeMode: AdminFeeMode.None,
    adminFee: 0,
    hourlyFee: 0,
    hourlyFeePercent: 0,
    leasePercent: 0,

    do50Hr: true,
    do100Hr: true,
    doPitotStatic: false,
    ...init
  }
}

export interface Info {
  tailNumber: string
  serialNumber: string
  seats: number
  make: string
  model: string
  category: Category
  class: AircraftClass
  picture: string
  location: string
  mfrYear: number
  price: number
  regExpiration: Date | null
  rentalPrice: number
  /** Complex, TAA, High Performance, … — the Aircraft Type list on the Info card. */
  aircraftTypes: AircraftType[]
}

export function createInfo(init: Partial<Info> = {}): Info {
  return {
    tailNumber: '',
    serialNumber: '',
    seats: 2,
    make: '',
    model: '',
    category: Category.Airplane,
    class: AircraftClass.sel, // default to SEL
    picture: '',
    location: '',
    mfrYear: 0,
    price: 0,
    regExpiration: null,
    rentalPrice: 0,
    aircraftTypes: [] as AircraftType[],

    ...init
  }
}

export interface Equipment {
  otherEquipment: string
  hasTransponder: boolean
  hasELT: boolean
  hasGPS: boolean
  eltHexCode: string
  adsbnum: string
  modesCode: string
  recordStorage: string
  hasKeyLocker: boolean
  keyNumber: number
  Locked: boolean
  /**
   * How finely this aircraft's tach reads: tenths or hundredths. Every tach
   * entry for the aircraft allows only that many decimals, and readings are
   * saved to it (utils/readings.ts — tachPlacesFor). Hobbs is always tenths.
   */
  tachPrecision: TachPrecision
}

export function createEquipment(init: Partial<Equipment> = {}): Equipment {
  return {
    otherEquipment: '',
    hasTransponder: true,
    hasELT: true,
    hasGPS: true,
    eltHexCode: '',
    adsbnum: '',
    modesCode: '',
    recordStorage: '',
    hasKeyLocker: false,
    keyNumber: 0,
    Locked: true, // default to locked
    tachPrecision: DEFAULT_TACH_PRECISION,
    ...init
  }
}

export interface Owner {
  ownerID: string
  ownerType: 'account' | 'client'
  name: string
  phone: string
  insuranceCo: string
  insuranceNum: string
  insuranceExp: Date | null
  email: string
  ownerSurcharge: number
  acManager: AcManager
  // Decimal 0-1 (e.g. 0.6 = 60%). Only meaningful when the aircraft has
  // MORE THAN ONE owner — a single owner implicitly gets 100% and this
  // field should stay unset for them. Shares are NOT required to sum to 1
  // across an aircraft's owners (some ownership may be unassigned).
  sharePercent?: number
}

export function createOwner(init: Partial<Owner> = {}): Owner {
  return {
    ownerID: '',
    ownerType: 'account',
    name: '',
    phone: '',
    insuranceCo: '',
    insuranceNum: '',
    insuranceExp: null,
    acManager: createAcManager(),
    email: '',
    ownerSurcharge: 0,
    ...init
  }
}

export interface AcManager {
  name: string
  email: string
  phone: string
}

export function createAcManager(init: Partial<AcManager> = {}): AcManager {
  return {
    name: '',
    email: '',
    phone: '',
    ...init
  }
}

// Convert MaintenanceToDate to Firebase-compatible format
export function maintenanceToFirebase(maint: MaintenanceToDate): {} {
  const toTimestamp = (date: Date | null): Timestamp | null =>
    date ? Timestamp.fromDate(date) : null

  return {
    hobbs: maint.hobbs,
    tach: maint.tach,

    airframeInfo: maint.airframeInfo
      ? {
          annualDate: toTimestamp(maint.airframeInfo.annualDate),
          annualTach: maint.airframeInfo.annualTach,
          annualHobbs: maint.airframeInfo.annualHobbs,
          hundredHour: toTimestamp(maint.airframeInfo.hundredHour),
          hundredHourTach: maint.airframeInfo.hundredHourTach,
          hundredHourHobbs: maint.airframeInfo.hundredHourHobbs,
          fiftyHour: toTimestamp(maint.airframeInfo.fiftyHour),
          fiftyHourTach: maint.airframeInfo.fiftyHourTach,
          fiftyHourHobbs: maint.airframeInfo.fiftyHourHobbs
        }
      : null,
    engineInfo: maint.engineInfo
      ? {
          annualDate: toTimestamp(maint.engineInfo.annualDate),
          annualTach: maint.engineInfo.annualTach,
          annualHobbs: maint.engineInfo.annualHobbs,
          hundredHour: toTimestamp(maint.engineInfo.hundredHour),
          hundredHourTach: maint.engineInfo.hundredHourTach,
          hundredHourHobbs: maint.engineInfo.hundredHourHobbs,
          fiftyHour: toTimestamp(maint.engineInfo.fiftyHour),
          fiftyHourTach: maint.engineInfo.fiftyHourTach,
          fiftyHourHobbs: maint.engineInfo.fiftyHourHobbs
        }
      : null,
    propInfo: maint.propInfo
      ? {
          annualDate: toTimestamp(maint.propInfo.annualDate),
          annualTach: maint.propInfo.annualTach,
          annualHobbs: maint.propInfo.annualHobbs,
          hundredHour: toTimestamp(maint.propInfo.hundredHour),
          hundredHourTach: maint.propInfo.hundredHourTach,
          hundredHourHobbs: maint.propInfo.hundredHourHobbs,
          fiftyHour: toTimestamp(maint.propInfo.fiftyHour),
          fiftyHourTach: maint.propInfo.fiftyHourTach,
          fiftyHourHobbs: maint.propInfo.fiftyHourHobbs
        }
      : null,

    gpsExpirationDate: toTimestamp(maint.gpsExpirationDate),
    eltBatteryExpDate: toTimestamp(maint.eltBatteryExpDate),
    eltInspection: toTimestamp(maint.eltInspection),
    pitotStaticInspection: toTimestamp(maint.pitotStaticInspection),
    transponderInspection: toTimestamp(maint.transponderInspection),

    engineReplaceTach: maint.engineReplaceTach,
    propReplaceTach: maint.propReplaceTach,

    tachReplaceTach: maint.tachReplaceTach,
    hobbsReplaceTach: maint.hobbsReplaceTach
  }
}

// Convert Firebase data to MaintenanceToDate
export function maintenanceFromFirebase(maint: any): MaintenanceToDate {
  const toDate = (timestamp: Timestamp | null): Date | null =>
    timestamp ? timestamp.toDate() : null

  return {
    hobbs: maint.hobbs,
    tach: maint.tach,

    airframeInfo: {
      annualDate: toDate(maint.airframeInfo.annualDate),
      annualTach: maint.airframeInfo.annualTach,
      annualHobbs: maint.airframeInfo.annualHobbs,
      hundredHour: toDate(maint.airframeInfo.hundredHour),
      hundredHourTach: maint.airframeInfo.hundredHourTach,
      hundredHourHobbs: maint.airframeInfo.hundredHourHobbs,
      fiftyHour: toDate(maint.airframeInfo.fiftyHour),
      fiftyHourTach: maint.airframeInfo.fiftyHourTach,
      fiftyHourHobbs: maint.airframeInfo.fiftyHourHobbs
    },
    engineInfo: {
      annualDate: toDate(maint.engineInfo.annualDate),
      annualTach: maint.engineInfo.annualTach,
      annualHobbs: maint.engineInfo.annualHobbs,
      hundredHour: toDate(maint.engineInfo.hundredHour),
      hundredHourTach: maint.engineInfo.hundredHourTach,
      hundredHourHobbs: maint.engineInfo.hundredHourHobbs,
      fiftyHour: toDate(maint.engineInfo.fiftyHour),
      fiftyHourTach: maint.engineInfo.fiftyHourTach,
      fiftyHourHobbs: maint.engineInfo.fiftyHourHobbs
    },
    propInfo: {
      annualDate: toDate(maint.propInfo.annualDate),
      annualTach: maint.propInfo.annualTach,
      annualHobbs: maint.propInfo.annualHobbs,
      hundredHour: toDate(maint.propInfo.hundredHour),
      hundredHourTach: maint.propInfo.hundredHourTach,
      hundredHourHobbs: maint.propInfo.hundredHourHobbs,
      fiftyHour: toDate(maint.propInfo.fiftyHour),
      fiftyHourTach: maint.propInfo.fiftyHourTach,
      fiftyHourHobbs: maint.propInfo.fiftyHourHobbs
    },

    gpsExpirationDate: toDate(maint.gpsExpirationDate),
    eltBatteryExpDate: toDate(maint.eltBatteryExpDate),
    eltInspection: toDate(maint.eltInspection),
    pitotStaticInspection: toDate(maint.pitotStaticInspection),
    transponderInspection: toDate(maint.transponderInspection),

    engineReplaceTach: maint.engineReplaceTach,
    propReplaceTach: maint.propReplaceTach,

    tachReplaceTach: maint.tachReplaceTach,
    hobbsReplaceTach: maint.hobbsReplaceTach,

    inMaintenance: maint.inMaintenance ? maint.inMaintenance : false
  }
}

export interface Currency {
  days: number
  type: CurrencyType
}

export interface Requirements {
  currency: Currency
  totalHours: number
  checks: AircraftType[]
  isSoloAllowed: boolean
  managementSignOff: boolean
  signOff: boolean
}

export function createRequirements(init: Partial<Requirements> = {}): Requirements {
  return {
    currency: { days: 0, type: CurrencyType.None },
    checks: [],
    totalHours: 0,
    isSoloAllowed: false,
    managementSignOff: false,
    signOff: false,

    ...init
  }
}

export interface InstructorRequirements {
  requiresCfii: boolean
  totalHours: number
}

export function createCfiRequirements(
  init: Partial<InstructorRequirements> = {}
): InstructorRequirements {
  return {
    requiresCfii: false,
    totalHours: 0,
    ...init
  }
}

export interface AircraftMaintenanceItems {
  tailNumber: string
  model: string
  status: string
  lastMaintenance: string
  tach: number
  hobbs: number
  criticalItems: string[]
  annualDate: Date | null
  hundredHourTach: number | null
  fiftyHourTach: number | null
  eltInspection: Date | null
  pitotStaticInspection: Date | null
  transponderInspection: Date | null
  gpsExpirationDate: Date | null
  regExpiration: Date | null
}
